<script setup>
// ContactsTable.vue — visao "lista densa" estilo ClickUp pra /contacts.
// Substitui o card chunky por uma linha ~44px com checkbox, avatar,
// nome, e colunas alinhadas a direita (ultima atividade, telefone,
// e-mail, localizacao, status). Hover revela 3 acoes inline
// (ver, editar, excluir).
//
// Selecao e mantida em ref local no parent (ContactsIndex) e
// passada via v-model:selected (Set). Linha clicavel = navegar pro
// detalhe (mesmo handler do antigo "Ver detalhes").
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  contacts: { type: Array, required: true },
  selected: { type: Set, default: () => new Set() },
});

const emit = defineEmits(['update:selected', 'view', 'edit', 'delete']);

const { t } = useI18n();

const hoveredId = ref(null);

// `countries` (shared/constants/countries.js) so tem o campo `id`
// (ISO-2). Indexar por `code` empurra todos os paises pra mesma
// chave `undefined`, fazendo o ultimo registro vazar como fallback
// pra qualquer contato sem pais (bug "Zimbabwe pra todo mundo").
const countriesMap = computed(() =>
  countries.reduce((acc, c) => {
    if (c.id) acc[c.id] = c;
    return acc;
  }, {})
);

const findCountry = attrs => {
  if (!attrs) return null;
  const { country, countryCode } = attrs;
  return (
    (country && countriesMap.value[country]) ||
    (countryCode && countriesMap.value[countryCode]) ||
    null
  );
};

const allSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(c => props.selected.has(c.id))
);

const someSelected = computed(
  () => props.contacts.some(c => props.selected.has(c.id)) && !allSelected.value
);

const toggleAll = () => {
  const next = new Set(props.selected);
  if (allSelected.value) {
    props.contacts.forEach(c => next.delete(c.id));
  } else {
    props.contacts.forEach(c => next.add(c.id));
  }
  emit('update:selected', next);
};

const toggleOne = id => {
  const next = new Set(props.selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  emit('update:selected', next);
};

const isSelected = id => props.selected.has(id);

const formatActivity = ts => (ts ? dynamicTime(ts) : '—');

const locationLabel = attrs => {
  if (!attrs) return null;
  const c = findCountry(attrs);
  const { city } = attrs;
  if (!c) return city || null;
  return city ? `${city}, ${c.name}` : c.name;
};

const locationCountryCode = attrs => findCountry(attrs)?.id || null;

const onRowClick = (event, id) => {
  // Ignora clique em checkbox/botoes — handled separadamente.
  if (event.target.closest('[data-row-action]')) return;
  emit('view', id);
};

const onHoverEnter = id => {
  hoveredId.value = id;
};
const onHoverLeave = () => {
  hoveredId.value = null;
};
</script>

<template>
  <div class="w-full">
    <!-- Header de colunas (sticky abaixo da toolbar) -->
    <div
      class="sticky top-0 z-[5] grid grid-cols-[36px_minmax(0,1fr)_140px_160px_220px_180px_44px] items-center gap-3 px-4 h-9 border-b border-n-weak bg-n-background text-[11px] font-medium uppercase tracking-wide text-n-slate-11"
    >
      <div class="flex items-center justify-center">
        <input
          type="checkbox"
          :checked="allSelected"
          :indeterminate.prop="someSelected"
          class="!h-4 !w-4 !m-0 cursor-pointer accent-n-brand rounded border-n-weak"
          :aria-label="t('CONTACTS_LAYOUT.TABLE.SELECT_ALL')"
          @change="toggleAll"
        />
      </div>
      <span class="truncate">
        {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.NAME') }}
      </span>
      <span class="truncate">
        {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.LAST_ACTIVITY') }}
      </span>
      <span class="truncate">
        {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.PHONE') }}
      </span>
      <span class="truncate">
        {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.EMAIL') }}
      </span>
      <span class="truncate">
        {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.LOCATION') }}
      </span>
      <span class="sr-only">
        {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.ACTIONS') }}
      </span>
    </div>

    <!-- Linhas -->
    <div role="rowgroup">
      <div
        v-for="c in contacts"
        :key="c.id"
        role="row"
        class="grid grid-cols-[36px_minmax(0,1fr)_140px_160px_220px_180px_44px] items-center gap-3 px-4 h-11 border-b border-n-weak cursor-pointer transition-colors"
        :class="
          isSelected(c.id)
            ? 'bg-n-blue-3 hover:bg-n-blue-4'
            : 'hover:bg-n-alpha-1'
        "
        @click="onRowClick($event, c.id)"
        @mouseenter="onHoverEnter(c.id)"
        @mouseleave="onHoverLeave"
      >
        <div class="flex items-center justify-center" data-row-action>
          <input
            type="checkbox"
            :checked="isSelected(c.id)"
            class="!h-4 !w-4 !m-0 cursor-pointer accent-n-brand rounded border-n-weak"
            :aria-label="t('CONTACTS_LAYOUT.TABLE.SELECT_ROW')"
            @change="toggleOne(c.id)"
            @click.stop
          />
        </div>

        <!-- Nome + avatar + empresa (truncate) -->
        <div class="flex items-center gap-2.5 min-w-0">
          <Avatar
            :name="c.name"
            :src="c.thumbnail"
            :size="28"
            :status="c.availabilityStatus"
            hide-offline-status
            rounded-full
          />
          <div class="flex flex-col min-w-0 leading-tight">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ c.name || t('CONTACTS_LAYOUT.TABLE.UNNAMED') }}
            </span>
            <span
              v-if="c.additionalAttributes?.companyName"
              class="text-xs text-n-slate-11 truncate"
            >
              {{ c.additionalAttributes.companyName }}
            </span>
          </div>
        </div>

        <!-- Ultima atividade -->
        <span class="text-xs text-n-slate-11 truncate">
          {{ formatActivity(c.lastActivityAt) }}
        </span>

        <!-- Telefone -->
        <span class="text-xs text-n-slate-12 truncate tabular-nums">
          {{ c.phoneNumber || '—' }}
        </span>

        <!-- E-mail -->
        <span class="text-xs text-n-slate-12 truncate" :title="c.email || ''">
          {{ c.email || '—' }}
        </span>

        <!-- Localizacao -->
        <span class="flex items-center gap-1.5 text-xs text-n-slate-11 min-w-0">
          <Flag
            v-if="locationCountryCode(c.additionalAttributes)"
            :country="locationCountryCode(c.additionalAttributes)"
            class="size-3.5 shrink-0"
          />
          <span class="truncate">
            {{ locationLabel(c.additionalAttributes) || '—' }}
          </span>
        </span>

        <!-- Acoes inline (aparece no hover) -->
        <div
          class="flex items-center justify-end gap-0.5 transition-opacity"
          :class="hoveredId === c.id ? 'opacity-100' : 'opacity-0'"
          data-row-action
        >
          <button
            v-tooltip="t('CONTACTS_LAYOUT.TABLE.ACTIONS.EDIT')"
            type="button"
            class="inline-flex items-center justify-center !size-7 rounded text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click.stop="emit('edit', c.id)"
          >
            <span class="i-lucide-pencil size-4" />
          </button>
          <button
            v-tooltip="t('CONTACTS_LAYOUT.TABLE.ACTIONS.DELETE')"
            type="button"
            class="inline-flex items-center justify-center !size-7 rounded text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
            @click.stop="emit('delete', c)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
